import { ref, computed, watch, type Ref } from 'vue';
import { TicketFiltersDTOMatchMode, type FetchTicketResponse, type TicketFiltersDTO } from 'src/api/models';
import { postFetchAllTickets } from 'src/api/queries/order-portal-bff';
import { useNotification } from 'src/composables/useNotification';

const PAGE_SIZE = 20;

export function useTicketBoardSearch(
  searchInput: Ref<string | undefined>,
  unresolvedTickets: Ref<FetchTicketResponse[]>,
  unscheduledTickets: Ref<FetchTicketResponse[]>,
  pendingTickets: Ref<FetchTicketResponse[]>,
) {
  const globalResults = ref<FetchTicketResponse[]>([]);
  const isSearching = ref(false);
  const isLoadingMore = ref(false);
  const searchError = ref<string | null>(null);
  const currentPage = ref(1);
  const totalPages = ref(1);
  const { showError } = useNotification();

  let searchGeneration = 0;

  const isSearchActive = computed(() => (searchInput.value?.trim().length ?? 0) > 0);
  const hasMorePages = computed(() => currentPage.value < totalPages.value);

  function matchesSearch(ticket: FetchTicketResponse, query: string): boolean {
    const q = query.toLowerCase();
    if (ticket.title?.toLowerCase().includes(q)) return true;
    if (ticket.description?.toLowerCase().includes(q)) return true;
    const num = parseInt(query, 10);
    if (!isNaN(num) && ticket.number === num) return true;
    return false;
  }

  const filteredUnresolved = computed(() => {
    const query = searchInput.value?.trim() || '';
    if (!query) return [];
    return unresolvedTickets.value.filter(t => matchesSearch(t, query));
  });

  const filteredUnplanned = computed(() => {
    const query = searchInput.value?.trim() || '';
    if (!query) return [];
    return unscheduledTickets.value.filter(t => matchesSearch(t, query));
  });

  const filteredPending = computed(() => {
    const query = searchInput.value?.trim() || '';
    if (!query) return [];
    return pendingTickets.value.filter(t => matchesSearch(t, query));
  });

  const filteredScheduled = computed(() => {
    const localIds = new Set([
      ...filteredUnresolved.value.map(t => t.id),
      ...filteredUnplanned.value.map(t => t.id),
      ...filteredPending.value.map(t => t.id),
    ]);
    return globalResults.value.filter(t => !localIds.has(t.id));
  });

  function buildFilters(query: string): TicketFiltersDTO {
    const filters: TicketFiltersDTO = {
      match_mode: TicketFiltersDTOMatchMode.or,
      title: query,
      description: query,
    };
    const parsed = parseInt(query, 10);
    if (!isNaN(parsed)) {
      filters.number = parsed;
    }
    return filters;
  }

  async function executeSearch(query: string) {
    const thisGeneration = ++searchGeneration;

    if (!query) {
      globalResults.value = [];
      searchError.value = null;
      currentPage.value = 1;
      totalPages.value = 1;
      return;
    }

    isSearching.value = true;
    searchError.value = null;
    currentPage.value = 1;
    totalPages.value = 1;

    try {
      const response = await postFetchAllTickets(buildFilters(query), { limit: PAGE_SIZE, page: 1 });

      if (thisGeneration !== searchGeneration) return;

      globalResults.value = (response?.data ?? []) as unknown as FetchTicketResponse[];
      totalPages.value = response?.total_pages ?? 1;
      currentPage.value = 1;
    } catch (error) {
      if (thisGeneration !== searchGeneration) return;

      searchError.value = 'Failed to search tickets';
      showError({ message: 'Failed to search tickets' });
      globalResults.value = [];
    } finally {
      if (thisGeneration === searchGeneration) {
        isSearching.value = false;
      }
    }
  }

  async function loadMoreResults() {
    const query = searchInput.value?.trim() || '';
    if (!query || !hasMorePages.value || isLoadingMore.value) return;

    const thisGeneration = searchGeneration;
    const nextPage = currentPage.value + 1;
    isLoadingMore.value = true;

    try {
      const response = await postFetchAllTickets(buildFilters(query), { limit: PAGE_SIZE, page: nextPage });

      if (thisGeneration !== searchGeneration) return;

      const newTickets = (response?.data ?? []) as unknown as FetchTicketResponse[];
      globalResults.value = [...globalResults.value, ...newTickets];
      currentPage.value = nextPage;
      totalPages.value = response?.total_pages ?? totalPages.value;
    } catch (error) {
      if (thisGeneration !== searchGeneration) return;
      showError({ message: 'Failed to load more results' });
    } finally {
      if (thisGeneration === searchGeneration) {
        isLoadingMore.value = false;
      }
    }
  }

  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  function debouncedSearch(query: string) {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      executeSearch(query);
    }, 300);
  }

  function cancelPendingSearch() {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    searchGeneration++;
  }

  watch(searchInput, (val) => {
    const query = val?.trim() || '';
    if (!query) {
      cancelPendingSearch();
      globalResults.value = [];
      searchError.value = null;
      isSearching.value = false;
      isLoadingMore.value = false;
      currentPage.value = 1;
      totalPages.value = 1;
      return;
    }
    isSearching.value = true;
    debouncedSearch(query);
  });

  return {
    isSearchActive,
    isSearching,
    isLoadingMore,
    searchError,
    hasMorePages,
    filteredUnresolved,
    filteredUnplanned,
    filteredPending,
    filteredScheduled,
    loadMoreResults,
  };
}
